/* eslint-disable no-await-in-loop */
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { confirmUnauthorizedAndRedirect } from '../../dialogs/actions';
import { ResponseError } from '../../modules/request';
import requestJson from '../../modules/requestJson';
import uuid from '../../modules/uuid';
import { selectLatestMultiUserPollId, selectMultiUserClientId } from './selectors';
import {
  MultiUserClientOptions,
  MultiUserEvent,
  MultiUserEventType,
  MultiUserPollRequest,
  MultiUserPollResponse,
  MultiUserRootState,
  MultiUserState,
} from './types';

/**
 * Action for initializing the multi-user client with
 * certain configuration.
 */
export const initializeMultiUserClient = createAction(
  'multiuser/initializeClient',
  (options: Omit<MultiUserClientOptions, 'clientId'>) => {
    const payload: MultiUserClientOptions = {
      clientId: uuid(),
      ...options,
    };
    return { payload };
  },
);
/**
 * Action for that is dispatched when any multi-user event is received
 * from another user.
 */
export const receiveMultiUserEvent = createAction<MultiUserPollResponse>('multiuser/receiveEvent');

type PollParameters = Pick<MultiUserEvent, 'type' | 'details'>;

async function performMultiUserPollRequest(state: MultiUserState, event: PollParameters | null) {
  const { options, latestCursor } = state;
  if (!options) {
    throw new Error('Cannot poll multi-user events before configured with `initializeMultiUserClient`');
  }
  const { appId, clientId, userDisplayName } = options;
  const body: MultiUserPollRequest = {
    cursor: latestCursor ?? undefined,
    user_uuid: clientId,
    user_display_name: userDisplayName,
  };
  if (event) {
    body.event = {
      ...event,
      user_display_name: userDisplayName,
      user_uuid: clientId,
    };
  }
  return requestJson<MultiUserPollResponse>('POST', `/transdolan/dolan_api/v1/apps/${appId}/events.json`, body);
}

/**
 * This async thunks starts a poll / request send loop. It sends any given event and then continues
 * long-polling events from the server until another poll occurs.
 */
export const pollMultiUserEvent = createAsyncThunk<void, PollParameters | null, { state: MultiUserRootState }>(
  'multiuser/pollEvent',
  async (pollEvent, { dispatch, getState, requestId }) => {
    let requestEvent: PollParameters | null = pollEvent;
    do {
      try {
        const result = await performMultiUserPollRequest(getState().multiuser, requestEvent);
        if (selectLatestMultiUserPollId(getState()) !== requestId) {
          // Another parallel poll has been started. Cancel this
          return;
        }
        // Dispatch action for receiving a valid multi-user poll response
        dispatch(receiveMultiUserEvent(result));
        // Do not send the event again on the next poll
        requestEvent = null;
      } catch (error) {
        if (error instanceof ResponseError && error.response.status === 401) {
          // Unauthorized, probably session expired
          await dispatch(confirmUnauthorizedAndRedirect());
          throw error;
        }
        // caught long poll error
        await new Promise((resolve) => {
          setTimeout(resolve, Math.round(9000 + Math.random() * 4000));
        });
      }
      // wait for a while until the next poll
      await new Promise((resolve) => {
        setTimeout(resolve, Math.round(1000 + Math.random() * 1000));
      });
    } while (pollEvent?.type !== 'close');
  },
  {
    condition(_args, api) {
      // Only poll events if client has been set up
      const clientId = selectMultiUserClientId(api.getState());
      return clientId != null;
    },
  },
);

export function sendMultiUserEvent(eventType: 'save', changelog: string): ReturnType<typeof pollMultiUserEvent>;
export function sendMultiUserEvent(eventType: 'close'): ReturnType<typeof pollMultiUserEvent>;
export function sendMultiUserEvent(
  eventType: 'open_page',
  payload: { name: string; unsaved_changes: boolean },
): ReturnType<typeof pollMultiUserEvent>;
export function sendMultiUserEvent(eventType: 'unsaved_changes'): ReturnType<typeof pollMultiUserEvent>;
export function sendMultiUserEvent(eventType: 'no_changes'): ReturnType<typeof pollMultiUserEvent>;
/**
 * Shortcut action creator for sending a multi-user event of certain kind.
 */
export function sendMultiUserEvent(
  eventType: MultiUserEventType,
  eventDetails?: unknown,
): ReturnType<typeof pollMultiUserEvent> {
  return pollMultiUserEvent({ type: eventType, details: eventDetails });
}
